import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import packageJson from '../../package.json';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getMemberByToken,
  getMemberInvolvements,
  getMinistries,
  getMinistryAreasByGender,
  updateMember,
  submitMinistryInvolvements,
} from '../api';

const toChecked = (v) => v === true || v === 1 || v === '1' || v === 'true';

function getLeaderDisplayName(l) {
  if (!l) return null;
  if (l.group_id != null && l.group) return l.group.name;
  if (l.member) return `${l.member.first_name} ${l.member.last_name}`;
  return null;
}

function formatPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

const showMinistryAreaParticipation = process.env.REACT_APP_SHOW_MINISTRY_AREA_PARTICIPATION === '1';

function InvolvementFormPage() {
  const { memberId: tokenOrMemberId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [member, setMember] = useState(null);
  const [mostRecentInvolvements, setMostRecentInvolvements] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [ministryAreasByMinistry, setMinistryAreasByMinistry] = useState({});
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [sundayAm, setSundayAm] = useState(false);
  const [sundayPm, setSundayPm] = useState(false);
  const [wednesdayPm, setWednesdayPm] = useState(false);
  const [serviceAttendanceEdited, setServiceAttendanceEdited] = useState(false);
  const [newInvolvements, setNewInvolvements] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [tutorialChecked1, setTutorialChecked1] = useState(true);
  const [tutorialChecked2, setTutorialChecked2] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const ministrySteps = useMemo(() => {
    return ministries.filter((min) => {
      const ministryAreas = ministryAreasByMinistry[min.id] || [];
      return ministryAreas.length > 0;
    });
  }, [ministries, ministryAreasByMinistry]);

  const totalSteps = 4 + ministrySteps.length;

  const loadData = useCallback(async () => {
    if (!tokenOrMemberId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMemberByToken(tokenOrMemberId);
      const memberRes = data.member;
      const [involvementsRes, ministriesRes] = await Promise.all([
        getMemberInvolvements(memberRes.id, { pageSize: 500, sortBy: 'created_at', sortOrder: 'desc' }),
        getMinistries(),
      ]);
      setMember(memberRes);
      updateMember(memberRes.id, { form_status: 'opened' }).catch(() => {});
      setSundayAm(toChecked(memberRes.sunday_am));
      setSundayPm(toChecked(memberRes.sunday_pm));
      setMostRecentInvolvements(involvementsRes.data || involvementsRes.involvements || []);
      const activeMinistries = (ministriesRes || []).filter((m) => m.is_active !== false && m.is_active !== 0);
      const sortedMinistries = [...activeMinistries].sort((a, b) => {
        const aOrder = a.display_order ?? 999999;
        const bOrder = b.display_order ?? 999999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      });
      setMinistries(sortedMinistries);

      const gender = memberRes.gender;
      const ministryAreasRaw = await getMinistryAreasByGender(gender);
      const ministryAreas = (ministryAreasRaw || []).filter((m) => m.is_active !== false && m.is_active !== 0);
      const byMinistry = {};
      ministryAreas.forEach((m) => {
        if (!byMinistry[m.ministry_id]) byMinistry[m.ministry_id] = [];
        byMinistry[m.ministry_id].push(m);
      });
      // Sort each ministry's areas: display_order (nulls last), then name
      Object.keys(byMinistry).forEach((mid) => {
        byMinistry[mid].sort((a, b) => {
          const aOrder = a.display_order ?? 999999;
          const bOrder = b.display_order ?? 999999;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return (a.name || '').localeCompare(b.name || '');
        });
      });
      setMinistryAreasByMinistry(byMinistry);
    } catch (err) {
      setError(err.isExpired ? 'This access token has expired. Please contact Kip Nickell or Trey England to get a new token and link.' : err.message);
    } finally {
      setLoading(false);
    }
  }, [tokenOrMemberId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Re-fetch when page is restored from bfcache (e.g. back button)
  useEffect(() => {
    const handlePageshow = (e) => {
      if (e.persisted && tokenOrMemberId) loadData();
    };
    window.addEventListener('pageshow', handlePageshow);
    return () => window.removeEventListener('pageshow', handlePageshow);
  }, [tokenOrMemberId, loadData]);

  useEffect(() => {
    if (member) {
      setServiceAttendanceEdited(false);
      setSundayAm(toChecked(member.sunday_am));
      setSundayPm(toChecked(member.sunday_pm));
      setWednesdayPm(toChecked(member.wednesday_pm));
    }
  }, [member]);

  useEffect(() => {
    if (step === 2) {
      setTutorialChecked1(true);
      setTutorialChecked2(false);
    }
  }, [step]);

  const displaySundayAm = member && !serviceAttendanceEdited ? toChecked(member.sunday_am) : sundayAm;
  const displaySundayPm = member && !serviceAttendanceEdited ? toChecked(member.sunday_pm) : sundayPm;
  const displayWednesdayPm = member && !serviceAttendanceEdited ? toChecked(member.wednesday_pm) : wednesdayPm;

  const mostRecentMinistryAreaIds = useMemo(() => {
    return new Set((mostRecentInvolvements || []).map((i) => i.ministry_area_id || i.ministry_area?.id));
  }, [mostRecentInvolvements]);

  const handleMinistryAreaToggle = (ministryAreaId, ministryId) => {
    const key = `${ministryId}-${ministryAreaId}`;
    const current = newInvolvements[key] !== undefined ? newInvolvements[key] : mostRecentMinistryAreaIds.has(ministryAreaId);
    setNewInvolvements((prev) => ({ ...prev, [key]: !current }));
  };

  const handleSelectAll = (ministryId, checked) => {
    const ministryAreas = ministryAreasByMinistry[ministryId] || [];
    const updates = {};
    ministryAreas.forEach((m) => {
      updates[`${ministryId}-${m.id}`] = checked;
    });
    setNewInvolvements((prev) => ({ ...prev, ...updates }));
  };

  const isMinistryAreaChecked = (ministryAreaId, ministryId) => {
    const key = `${ministryId}-${ministryAreaId}`;
    if (newInvolvements[key] !== undefined) return newInvolvements[key];
    return mostRecentMinistryAreaIds.has(ministryAreaId);
  };

  const handleNext = async () => {
    if (step >= totalSteps - 1) return;
    setSubmitting(true);
    setError(null);
    try {
      if (step === 1) {
        await updateMember(member.id, {
          sunday_am: displaySundayAm,
          sunday_pm: displaySundayPm,
          wednesday_pm: displayWednesdayPm,
        });
      } else if (step === 2) {
        await updateMember(member.id, { form_status: 'in-progress' });
      } else if (step >= 3 && step < 3 + ministrySteps.length) {
        const min = ministrySteps[step - 3];
        const ministryAreas = ministryAreasByMinistry[min.id] || [];
        const ministryAreaIds = ministryAreas
          .filter((m) => isMinistryAreaChecked(m.id, min.id))
          .map((m) => m.id);
        await submitMinistryInvolvements(member.id, min.id, { ministry_areas: ministryAreaIds });
      }
      setStep((s) => s + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const handleRestart = () => {
    setNameConfirmed(false);
    setSundayAm(false);
    setSundayPm(false);
    setWednesdayPm(false);
    setServiceAttendanceEdited(false);
    setNewInvolvements({});
    setTutorialChecked1(true);
    setTutorialChecked2(false);
    setStep(0);
    setSubmitSuccess(false);
    setError(null);
    if (tokenOrMemberId) loadData();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await updateMember(member.id, {
        form_completed_at: new Date().toISOString().slice(0, 10),
        form_status: 'closed',
      });
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !member) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/wh-logo.png`}
          alt="Walter Hill Church"
          sx={{ height: 48, display: 'block', mx: 'auto', mb: 2 }}
        />
        <Alert severity="error" sx={{ textAlign: 'left' }}>
          {error}
        </Alert>
        <Button component={Link} to="/" variant="outlined" sx={{ mt: 2 }}>
          Return to Home
        </Button>
      </Box>
    );
  }

  if (submitSuccess) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/wh-logo.png`}
          alt="Church logo - click to restart"
          onClick={handleRestart}
          sx={{
            height: 48,
            cursor: 'pointer',
            display: 'block',
            mx: 'auto',
            mb: 2,
            '&:hover': { opacity: 0.85 },
          }}
        />
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            Thank you!
          </Typography>
          <Typography>Your information has been submitted successfully. 
            If you have any questions, please contact Kip Nickell or Trey England.
          </Typography>
        </Paper>
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 2, opacity: 0.8 }}
        >
          v{packageJson.version}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', p: 2, pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/wh-logo.png`}
          alt="Church logo - click to restart"
          onClick={handleRestart}
          sx={{
            height: 48,
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 },
          }}
        />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Involvement Preferences
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        {step === 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                This link is valid for 30 days.
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            We're grateful for all the ways our members serve, and we want to understand the areas in 
            which each member would like to be involved. The purpose of this form is to indicate the 
            areas where you would be interested in serving. These responses help connect members to 
            ministries here at Walter Hill. 
            Thank you for your willingness to serve and be an active part of our church family!
            Please fill out this form. 
            It should take only a few minutes to complete.
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
              {member?.first_name} {member?.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {member?.email}
            </Typography>
            {member?.phone && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatPhone(member.phone)}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={nameConfirmed}
                  onChange={(e) => setNameConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label="Confirm name to continue."
              sx={{ mt: 2, display: 'block' }}
            />
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              What church service do you usually attend?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
              (At least one required)
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={displaySundayAm}
                  onChange={(e) => {
                    setServiceAttendanceEdited(true);
                    setSundayAm(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Sunday AM"
              sx={{ display: 'block' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={displaySundayPm}
                  onChange={(e) => {
                    setServiceAttendanceEdited(true);
                    setSundayPm(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Sunday PM"
              sx={{ display: 'block' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={displayWednesdayPm}
                  onChange={(e) => {
                    setServiceAttendanceEdited(true);
                    setWednesdayPm(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Wednesday PM"
              sx={{ display: 'block' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={displaySundayAm && displaySundayPm && displayWednesdayPm}
                  indeterminate={
                    (displaySundayAm || displaySundayPm || displayWednesdayPm) &&
                    !(displaySundayAm && displaySundayPm && displayWednesdayPm)
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setServiceAttendanceEdited(true);
                    setSundayAm(checked);
                    setSundayPm(checked);
                    setWednesdayPm(checked);
                  }}
                  color="primary"
                />
              }
              label="Select All"
              sx={{ display: 'block', mt: 1 }}
            />
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="h3" gutterBottom>
              Tutorial
            </Typography>
            {mostRecentMinistryAreaIds.size > 0 ? (
              <>
                <Typography variant="body1" paragraph>
                  We found data that you entered previously.  We will make you aware of ministry areas that you selected most recently. It will be presented to you as a green checkmark <CheckIcon sx={{ color: 'success.main', fontSize: 18, verticalAlign: 'middle' }} />. Here is an example below.
                </Typography>
                {(() => {
                  const recentInv = mostRecentInvolvements[0];
                  const recentName = recentInv?.ministry_area?.name || recentInv?.ministry_area_name || 'Ministry area you selected previously';
                  const allMinistryAreas = Object.values(ministryAreasByMinistry).flat();
                  const otherMinistryArea = allMinistryAreas.find((m) => !mostRecentMinistryAreaIds.has(m.id));
                  const otherName = otherMinistryArea?.name || 'Other ministry area option';
                  return (
                    <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0 }, mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ border: 0, fontWeight: 600, width: 24, px: 0.5 }}></TableCell>
                          <TableCell sx={{ border: 0, fontWeight: 600, width: 36, px: 0.5 }} />
                          <TableCell sx={{ border: 0, fontWeight: 600 }}>Ministry Area</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ width: 24, verticalAlign: 'middle', px: 0.5 }}>
                            <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          </TableCell>
                          <TableCell sx={{ width: 36, verticalAlign: 'middle', px: 0.5 }}>
                            <Checkbox
                              checked={tutorialChecked1}
                              onChange={(e) => setTutorialChecked1(e.target.checked)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{recentName}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ width: 24, verticalAlign: 'middle', px: 0.5 }} />
                          <TableCell sx={{ width: 36, verticalAlign: 'middle', px: 0.5 }}>
                            <Checkbox
                              checked={tutorialChecked2}
                              onChange={(e) => setTutorialChecked2(e.target.checked)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{otherName}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  );
                })()}
              </>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              This is a tutorial step.  This data above 
              will not be saved.  Select next to begin. 
            </Typography>
          </>
        )}

        {step >= 3 && step < 3 + ministrySteps.length && (
              <>
            {(() => {
              const min = ministrySteps[step - 3];
              const ministryAreas = ministryAreasByMinistry[min.id] || [];
              const hasAnyRecent = mostRecentMinistryAreaIds.size > 0;
              return (
                <>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="h3" gutterBottom sx={{ mb: 0 }}>
                      {min.name}
                    </Typography>
                    {min.description != null && min.description !== '' && (
                      <Tooltip
                        title={min.description}
                        arrow
                        placement={isMobile ? 'top' : 'right'}
                        open={isMobile ? openTooltipId === `min-${min.id}` : undefined}
                        onClose={isMobile ? () => setOpenTooltipId(null) : undefined}
                        disableFocusListener={isMobile}
                        disableHoverListener={isMobile}
                        disableTouchListener={isMobile}
                        slotProps={{ popper: { sx: { maxWidth: 'min(90vw, 320px)' } } }}
                      >
                        <IconButton
                          size="small"
                          sx={{ p: 1, minWidth: 44, minHeight: 44 }}
                          aria-label="Ministry description"
                          {...(isMobile && {
                            onClick: (e) => {
                              e.stopPropagation();
                              setOpenTooltipId((prev) => (prev === `min-${min.id}` ? null : `min-${min.id}`));
                            },
                          })}
                        >
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                    {(() => {
                      const ministryLeaderNames = (min.leaders || [])
                        .map((l) => getLeaderDisplayName(l))
                        .filter(Boolean);
                      return ministryLeaderNames.length > 0 ? (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 0.5, fontStyle: 'italic' }}>
                          {ministryLeaderNames.length === 1 ? 'Ministry Leader' : 'Ministry Leaders'}: {ministryLeaderNames.join(', ')}
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {ministryAreas.length > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Checkbox
                          checked={ministryAreas.every((m) => isMinistryAreaChecked(m.id, min.id))}
                          indeterminate={
                            ministryAreas.some((m) => isMinistryAreaChecked(m.id, min.id)) &&
                            !ministryAreas.every((m) => isMinistryAreaChecked(m.id, min.id))
                          }
                          onChange={(e) => handleSelectAll(min.id, e.target.checked)}
                          color="primary"
                          sx={{ py: 0 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: -0.5 }}>
                          Select all
                        </Typography>
                      </Box>
                    )}
                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                      {ministryAreas.map((m) => {
                        const hasRecent = mostRecentMinistryAreaIds.has(m.id);
                        const checked = isMinistryAreaChecked(m.id, min.id);
                        return (
                          <Box
                            component="li"
                            key={m.id}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              py: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:last-of-type': { borderBottom: 0 },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              {hasAnyRecent && (
                                hasRecent ? (
                                  <CheckIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
                                ) : (
                                  <Box sx={{ width: 20, height: 20, flexShrink: 0 }} />
                                )
                              )}
                              <Checkbox
                                checked={checked}
                                onChange={() => handleMinistryAreaToggle(m.id, min.id)}
                                color="primary"
                                sx={{ py: 0, flexShrink: 0 }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                <Typography component="span" variant="body1">
                                  {m.name}
                                </Typography>
                                {m.description != null && m.description !== '' && (
                                  <Tooltip
                                    title={m.description}
                                    arrow
                                    placement={isMobile ? 'top' : 'right'}
                                    open={isMobile ? openTooltipId === `ma-${min.id}-${m.id}` : undefined}
                                    onClose={isMobile ? () => setOpenTooltipId(null) : undefined}
                                    disableFocusListener={isMobile}
                                    disableHoverListener={isMobile}
                                    disableTouchListener={isMobile}
                                    slotProps={{ popper: { sx: { maxWidth: 'min(90vw, 320px)' } } }}
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.5, minWidth: 36, minHeight: 36 }}
                                      aria-label="Ministry area description"
                                      {...(isMobile && {
                                        onClick: (e) => {
                                          e.stopPropagation();
                                          setOpenTooltipId((prev) => (prev === `ma-${min.id}-${m.id}` ? null : `ma-${min.id}-${m.id}`));
                                        },
                                      })}
                                    >
                                      <InfoOutlinedIcon fontSize="small" color="action" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {showMinistryAreaParticipation && (m.involvements_count != null || m.members_involved_pct != null) && (
                                  <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {m.involvements_count ?? 0}
                                      {m.members_involved_pct != null && ` (${m.members_involved_pct}%)`}
                                    </Typography>
                                    {m.minimum_involvements != null && (m.involvements_count ?? 0) < m.minimum_involvements && (
                                      <Chip label={`Need ${m.minimum_involvements}`} size="small" color="info" sx={{ ml: 0.5, py: 0, px: 0.5, height: 18, fontSize: '0.7rem' }} />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            {m.leaders?.length > 0 && (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.3, pl: hasAnyRecent ? 6.5 : 4 }}>
                                Lead By: {m.leaders.map((l) => getLeaderDisplayName(l)).filter(Boolean).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  {hasAnyRecent && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic', display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                      Indicates that this was selected in the most recent form that we have on file. Provided to help you remember what you selected previously.
                    </Typography>
                  )}
                </>
              );
            })()}
          </>
        )}

        {step === totalSteps - 1 && (
          <>
            <Typography variant="h3" gutterBottom>
              Review your selections
            </Typography>
            {ministrySteps.map((min) => {
              const ministryAreas = ministryAreasByMinistry[min.id] || [];
              const selected = ministryAreas.filter((m) => isMinistryAreaChecked(m.id, min.id));
              if (selected.length === 0) return null;
              return (
                <Box key={min.id} sx={{ mb: 2 }}>
                  <Typography component="h5" variant="h5" sx={{ mb: 1 }}>
                    {min.name}
                  </Typography>
                  {selected.map((m) => (
                    <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', gap: '1px', ml: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckBoxIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography component="span">{m.name}</Typography>
                      </Box>
                      {m.leaders?.length > 0 && (
                        <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', pl: 2.5 }}>
                          Lead By: {m.leaders.map((l) => getLeaderDisplayName(l)).filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      <Typography
        variant="caption"
        component="div"
        color="text.secondary"
        textAlign="center"
        sx={{ mt: 2, mb: 1, opacity: 0.8 }}
      >
        v{packageJson.version}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button startIcon={<ChevronLeftIcon />} onClick={handleBack} disabled={step === 0}>
          Back
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textAlign: 'center' }}>
          Step {step + 1} of {(totalSteps || 1) + 1}
          {step === 0 ? null : ` (${Math.round(((step + 1) / ((totalSteps || 1) + 1)) * 100)}%)`}
        </Typography>
          {step < totalSteps - 1 ? (
            <Button
              variant="contained"
              endIcon={submitting ? <CircularProgress size={24} color="inherit" /> : <ChevronRightIcon />}
              onClick={handleNext}
              disabled={(step === 0 && !nameConfirmed) || (step === 1 && !displaySundayAm && !displaySundayPm && !displayWednesdayPm) || submitting}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'SUBMIT'}
            </Button>
          )}
      </Box>
    </Box>
  );
}

export default InvolvementFormPage;
